import { AnimatePresence } from 'framer-motion';
import { ArrowDown, ArrowUp, Eye, ImagePlus, Megaphone, Pencil, Plus, Trash2, Upload } from 'lucide-react';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { HOUSE_ADS, sceneFor, type HouseAd } from '../../ads/houseAds';
import { ApiError, endpoints, type AdInput, type AdminAd, type AdSchedule } from '../../services/api';
import { useSession } from '../../store/session';
import { HouseAdSlide } from '../HouseAdSlide';
import { SignInForm } from '../settings/AccountTab';
import { buttonClass, ghostButtonClass, inputClass, Toggle } from '../settings/controls';

const ADVERTISERS = ['Kirkpatrick Consult Limited', 'El Tiquetero'];
const MAX_EDGE = 3840; // 4K is plenty for TVs
const MAX_BYTES = 7.5 * 1024 * 1024;

/** Reads an image file as base64, re-encoding as JPEG if it's bigger than 4K or ~7.5 MB. */
async function prepareImage(file: File): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  let blob: Blob = file;
  if (scale < 1 || file.size > MAX_BYTES || !/^image\/(jpeg|png|webp|avif)$/.test(file.type)) {
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    blob = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Could not process image'))), 'image/jpeg', 0.88),
    );
  }
  bitmap.close();
  const buf = new Uint8Array(await blob.arrayBuffer());
  let binary = '';
  for (let i = 0; i < buf.length; i += 0x8000) binary += String.fromCharCode(...buf.subarray(i, i + 0x8000));
  return btoa(binary);
}

async function blobToBase64(blob: Blob): Promise<string> {
  const buf = new Uint8Array(await blob.arrayBuffer());
  let binary = '';
  for (let i = 0; i < buf.length; i += 0x8000) binary += String.fromCharCode(...buf.subarray(i, i + 0x8000));
  return btoa(binary);
}

const toPreview = (a: AdminAd, i: number): HouseAd => ({
  id: a.id,
  advertiser: a.advertiser,
  headline: a.headline,
  body: a.body,
  cta: a.cta,
  url: a.url,
  image: a.image,
  scene: sceneFor(i),
});

/** /admin – manage house ads (KCL, El Tiquetero, …). ADMIN_EMAILS accounts only. */
export function AdminPage() {
  const { load, loaded, user, config } = useSession();
  const [ads, setAds] = useState<AdminAd[] | null>(null);
  const [schedule, setSchedule] = useState<AdSchedule | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<AdminAd | 'new' | null>(null);
  const [preview, setPreview] = useState<HouseAd | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.documentElement.dataset.theme = 'dark';
    void load();
  }, [load]);

  const refresh = useCallback(async () => {
    try {
      const r = await endpoints.adminAds();
      setAds(r.ads);
      setSchedule(r.schedule);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError && e.status === 403 ? null : e instanceof Error ? e.message : 'Could not load ads');
    }
  }, []);

  useEffect(() => {
    if (user?.admin) void refresh();
  }, [user, refresh]);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  const [progress, setProgress] = useState<string | null>(null);

  // One ad per request: photos are up to ~3 MB each, too big to send all 24 at once.
  const importStarters = () =>
    run(async () => {
      let added = 0;
      for (const [i, a] of HOUSE_ADS.entries()) {
        setProgress(`Importing ${i + 1} of ${HOUSE_ADS.length}…`);
        let image: string | null = null;
        if (a.image) {
          const blob = await fetch(a.image).then((r) => (r.ok ? r.blob() : null)).catch(() => null);
          image = blob ? await blobToBase64(blob) : null;
        }
        const r = await endpoints.adminImportAds([
          { advertiser: a.advertiser, headline: a.headline, body: a.body, cta: a.cta, url: a.url, imageBrief: a.imageBrief ?? null, active: true, image },
        ]);
        added += r.added;
      }
      setProgress(null);
      setError(added ? null : 'All starter ads are already here.');
    });

  const move = (index: number, dir: -1 | 1) =>
    run(async () => {
      const list = [...(ads ?? [])];
      const other = list[index + dir];
      const me = list[index];
      if (!other || !me) return;
      // Renumber everything in steps of 10 with the pair swapped.
      [list[index], list[index + dir]] = [other, me];
      await Promise.all(list.map((a, i) => (a.sort === (i + 1) * 10 ? null : endpoints.adminUpdateAd(a.id, { sort: (i + 1) * 10 }))));
    });

  let content;
  if (!loaded) content = null;
  else if (!config?.features.accounts) content = <p className="muted">Accounts aren’t switched on for this server.</p>;
  else if (!user) content = <SignInForm returnTo="/admin" />;
  else if (!user.admin)
    content = (
      <div className="glass p-5 text-sm">
        <p>
          You’re signed in as <strong>{user.email}</strong>, which isn’t an ads administrator.
        </p>
        <p className="muted mt-2">Add this email to the server’s ADMIN_EMAILS setting to manage ads.</p>
      </div>
    );
  else
    content = (
      <>
        <div className="mb-6 flex flex-wrap gap-2">
          <button className={buttonClass} onClick={() => setEditing('new')} disabled={busy}>
            <Plus className="h-4 w-4" aria-hidden /> New ad
          </button>
          <button className={ghostButtonClass} onClick={() => void importStarters()} disabled={busy}>
            <Upload className="h-4 w-4" aria-hidden /> Import {HOUSE_ADS.length} starter ads
          </button>
        </div>
        {schedule && <ScheduleCard schedule={schedule} onSaved={setSchedule} />}
        {progress && <p className="muted mb-4 text-sm">{progress}</p>}
        {error && <p className="mb-4 text-sm text-red-400">{error}</p>}
        {ads && ads.length === 0 && (
          <p className="muted text-sm">No ads yet – screens are showing the built-in starter ads. Import them to add photos and edit.</p>
        )}
        <ul className="space-y-3">
          {ads?.map((a, i) => (
            <li key={a.id} className={`glass flex flex-wrap items-center gap-4 p-3 ${a.active ? '' : 'opacity-50'}`}>
              <div
                className="h-20 w-36 shrink-0 rounded-lg bg-cover bg-center"
                style={{ background: a.image ? `center / cover url("${a.image}")` : sceneFor(i) }}
                aria-hidden
              />
              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold uppercase tracking-wider text-[var(--accent)]">{a.advertiser}</div>
                <div className="truncate font-medium">{a.headline}</div>
                <div className="muted truncate text-xs">{a.image ? 'Photo uploaded' : 'No photo yet – gradient shown'}</div>
              </div>
              <div className="flex flex-wrap items-center gap-1">
                <button className={ghostButtonClass} onClick={() => void move(i, -1)} disabled={busy || i === 0} aria-label="Move up">
                  <ArrowUp className="h-4 w-4" aria-hidden />
                </button>
                <button className={ghostButtonClass} onClick={() => void move(i, 1)} disabled={busy || i === ads.length - 1} aria-label="Move down">
                  <ArrowDown className="h-4 w-4" aria-hidden />
                </button>
                <button className={ghostButtonClass} onClick={() => setPreview(toPreview(a, i))} aria-label="Preview">
                  <Eye className="h-4 w-4" aria-hidden />
                </button>
                <button className={ghostButtonClass} onClick={() => setEditing(a)}>
                  <Pencil className="h-4 w-4" aria-hidden /> Edit
                </button>
                <button
                  className={ghostButtonClass}
                  onClick={() => void run(() => endpoints.adminUpdateAd(a.id, { active: !a.active }))}
                  disabled={busy}
                >
                  {a.active ? 'Turn off' : 'Turn on'}
                </button>
              </div>
            </li>
          ))}
        </ul>
      </>
    );

  return (
    <div className="h-full overflow-y-auto bg-[var(--bg)]">
      <main className="mx-auto max-w-5xl px-6 py-10">
        <div className="mb-8 flex items-center gap-3">
          <Megaphone className="h-7 w-7 text-[var(--accent)]" aria-hidden />
          <h1 className="text-2xl font-semibold">House ads</h1>
          <a href="/" className="muted ml-auto text-sm hover:underline">
            Back to KCL Canvas
          </a>
        </div>
        {content}
      </main>

      {editing && (
        <AdEditor
          ad={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await refresh();
          }}
          onPreview={setPreview}
        />
      )}
      <AnimatePresence>
        {preview && (
          <div className="fixed inset-0 z-50">
            <HouseAdSlide ad={preview} onClose={() => setPreview(null)} onRemoveAds={() => undefined} />
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

const INTERVALS = [5, 10, 15, 20, 30, 45, 60, 90, 120, 180, 240, 360, 720, 1440];
const DURATIONS = [10, 15, 20, 30, 45, 60];

const everyLabel = (min: number) =>
  min < 60 ? `${min} minutes` : min === 60 ? 'hour' : min % 60 ? `${min / 60} hours` : `${min / 60} hours`;

/** How often screens on the free tier show an ad, for how long, and in which order. */
function ScheduleCard({ schedule, onSaved }: { schedule: AdSchedule; onSaved: (s: AdSchedule) => void }) {
  const [form, setForm] = useState(schedule);
  const [state, setState] = useState<'idle' | 'saving' | 'saved' | string>('idle');
  useEffect(() => setForm(schedule), [schedule]);
  const dirty = form.intervalMin !== schedule.intervalMin || form.durationSec !== schedule.durationSec || form.order !== schedule.order;
  const intervals = INTERVALS.includes(form.intervalMin) ? INTERVALS : [...INTERVALS, form.intervalMin].sort((a, b) => a - b);

  const save = async () => {
    setState('saving');
    try {
      onSaved((await endpoints.adminSetAdSchedule(form)).schedule);
      setState('saved');
    } catch (e) {
      setState(e instanceof Error ? e.message : 'Could not save');
    }
  };

  return (
    <section className="glass mb-6 p-4">
      <h2 className="mb-1 font-semibold">Schedule</h2>
      <p className="muted mb-4 text-sm">Applies to every free-tier screen. Screens pick up changes within about 30 minutes.</p>
      <div className="flex flex-wrap items-end gap-4">
        <label className="text-sm">
          <span className="muted mb-1 block">Show an ad every</span>
          <select className={inputClass} value={form.intervalMin} onChange={(e) => setForm({ ...form, intervalMin: Number(e.target.value) })}>
            {intervals.map((m) => (
              <option key={m} value={m}>
                {everyLabel(m)}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <span className="muted mb-1 block">For</span>
          <select className={inputClass} value={form.durationSec} onChange={(e) => setForm({ ...form, durationSec: Number(e.target.value) })}>
            {DURATIONS.map((d) => (
              <option key={d} value={d}>
                {d} seconds
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <span className="muted mb-1 block">Order</span>
          <select className={inputClass} value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value as AdSchedule['order'] })}>
            <option value="random">Random</option>
            <option value="sequence">In list order</option>
          </select>
        </label>
        <button className={buttonClass} onClick={() => void save()} disabled={!dirty || state === 'saving'}>
          {state === 'saving' ? 'Saving…' : 'Save schedule'}
        </button>
        {state === 'saved' && !dirty && <span className="muted text-sm">Saved</span>}
        {state !== 'idle' && state !== 'saving' && state !== 'saved' && <span className="text-sm text-red-400">{state}</span>}
      </div>
    </section>
  );
}

function AdEditor({
  ad,
  onClose,
  onSaved,
  onPreview,
}: {
  ad: AdminAd | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
  onPreview: (a: HouseAd) => void;
}) {
  const [form, setForm] = useState({
    advertiser: ad?.advertiser ?? ADVERTISERS[0]!,
    headline: ad?.headline ?? '',
    body: ad?.body ?? '',
    cta: ad?.cta ?? 'Find out more',
    url: ad?.url ?? 'https://kcltd.es',
    imageBrief: ad?.imageBrief ?? '',
    active: ad?.active ?? true,
  });
  const [image, setImage] = useState<{ b64: string; previewUrl: string } | null | undefined>(undefined); // undefined = unchanged
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const shownImage = image === undefined ? ad?.image ?? null : image?.previewUrl ?? null;

  const pick = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    try {
      const b64 = await prepareImage(file);
      setImage({ b64, previewUrl: URL.createObjectURL(file) });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not read that image');
    }
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const body: AdInput = { ...form, imageBrief: form.imageBrief || null };
    if (image !== undefined) body.image = image?.b64 ?? null;
    try {
      if (ad) await endpoints.adminUpdateAd(ad.id, body);
      else await endpoints.adminCreateAd(body);
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
      setBusy(false);
    }
  };

  const field = (key: 'headline' | 'body' | 'cta' | 'url', label: string, opts: { textarea?: boolean; max: number }) => (
    <label className="block text-sm">
      <span className="mb-1 block">{label}</span>
      {opts.textarea ? (
        <textarea className={inputClass} rows={2} maxLength={opts.max} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
      ) : (
        <input className={inputClass} maxLength={opts.max} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} required={key !== 'body'} />
      )}
    </label>
  );

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/60 p-6" onClick={onClose}>
      <form
        onSubmit={save}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl space-y-4 rounded-2xl bg-[var(--surface-strong)] p-6 shadow-2xl"
      >
        <h2 className="text-xl font-semibold">{ad ? 'Edit ad' : 'New ad'}</h2>

        <div
          className="flex aspect-video items-center justify-center rounded-xl bg-cover bg-center"
          style={{ background: shownImage ? `center / cover url("${shownImage}")` : sceneFor(0) }}
        >
          <label className={`${ghostButtonClass} cursor-pointer bg-black/50 text-white`}>
            <ImagePlus className="h-4 w-4" aria-hidden /> {shownImage ? 'Replace photo' : 'Upload photo'}
            <input type="file" accept="image/*" hidden onChange={(e) => void pick(e.target.files?.[0])} />
          </label>
        </div>
        {shownImage && (
          <button type="button" className="muted text-xs hover:underline" onClick={() => setImage(null)}>
            Remove photo
          </button>
        )}
        <p className="muted text-xs">Landscape, at least 1920×1080 (ideally 3840×2160). Large photos are resized to 4K automatically.</p>

        <label className="block text-sm">
          <span className="mb-1 block">Advertiser</span>
          <input
            className={inputClass}
            list="advertisers"
            value={form.advertiser}
            maxLength={60}
            required
            onChange={(e) => {
              const advertiser = e.target.value;
              const url = advertiser === 'El Tiquetero' && form.url.includes('kcltd.es') ? 'https://eltiquetero.com' : form.url;
              setForm({ ...form, advertiser, url });
            }}
          />
          <datalist id="advertisers">
            {ADVERTISERS.map((a) => (
              <option key={a} value={a} />
            ))}
          </datalist>
        </label>
        {field('headline', 'Headline', { max: 160 })}
        {field('body', 'Text', { textarea: true, max: 400 })}
        <div className="grid grid-cols-2 gap-3">
          {field('cta', 'Button', { max: 40 })}
          {field('url', 'Link (https://…) – also shown as a QR code', { max: 500 })}
        </div>
        <label className="block text-sm">
          <span className="mb-1 block">Photo brief (for whoever supplies the photo – not shown on screen)</span>
          <textarea className={inputClass} rows={2} maxLength={600} value={form.imageBrief} onChange={(e) => setForm({ ...form, imageBrief: e.target.value })} />
        </label>
        <Toggle label="Show this ad" checked={form.active} onChange={(active) => setForm({ ...form, active })} />

        {error && <p className="text-sm text-red-400">{error}</p>}
        <div className="flex flex-wrap justify-between gap-2 pt-2">
          <div className="flex gap-2">
            <button type="submit" className={buttonClass} disabled={busy}>
              Save
            </button>
            <button type="button" className={ghostButtonClass} onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className={ghostButtonClass}
              onClick={() => onPreview({ id: 'preview', ...form, image: shownImage, scene: sceneFor(0) })}
            >
              <Eye className="h-4 w-4" aria-hidden /> Preview
            </button>
          </div>
          {ad && (
            <button
              type="button"
              className={`${ghostButtonClass} border-red-400 text-red-400`}
              disabled={busy}
              onClick={async () => {
                if (!window.confirm('Delete this ad?')) return;
                setBusy(true);
                await endpoints.adminDeleteAd(ad.id).catch(() => undefined);
                await onSaved();
              }}
            >
              <Trash2 className="h-4 w-4" aria-hidden /> Delete
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
